import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
    Plane, 
    Globe, 
    Zap, 
    Shield, 
    ArrowRight,
    Check,
    MapPin
} from 'lucide-react';
import { ShareToSaveCard } from '../components/ShareToSaveCard';
import '../components/ShareToSave.css';

export const LandingPage = () => {
    const navigate = useNavigate();

    const features = [
        {
            icon: Globe,
            title: 'Azimuthal Equidistant Map',
            description: 'View flights on the same projection used by the United Nations, showing true distances from the center.'
        },
        {
            icon: Zap,
            title: 'Real-Time Tracking',
            description: 'Live flight data updated every few seconds, showing position, altitude, speed, and heading.'
        },
        {
            icon: Shield,
            title: 'Reliable Data',
            description: 'Powered by worldwide flight tracking networks for accurate and comprehensive coverage.'
        }
    ];

    const stats = [
        { value: '5000+', label: 'Active Flights' },
        { value: '195', label: 'Countries Covered' },
        { value: '24/7', label: 'Live Tracking' },
        { value: '100%', label: 'Worldwide Coverage' }
    ];

    return (
        <div className="min-h-screen bg-[#071D35]">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
                {/* Branding Logo as Background */}
                <div className="absolute inset-0 flex items-center justify-center opacity-10">
                    <img 
                        src="https://customer-assets.emergentagent.com/job_181a1683-971b-4c0d-99ad-dbc6848d04b9/artifacts/x2r5quht_AB%20site%20icon.png"
                        alt=""
                        className="w-[600px] h-[600px] object-contain"
                    />
                </div>
                <div className="absolute inset-0 hero-bg" />

                {/* Content */}
                <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#f07050]/10 border border-[#f07050]/30 mb-8">
                            <div className="w-2 h-2 rounded-full bg-[#f07050] animate-pulse"></div>
                            <span className="text-sm text-[#f07050] font-medium">
                                NEW AGE Flight Tracking
                            </span>
                        </div>

                        {/* Headline */}
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                            <span className="text-[#f07050]">Track Flights</span>
                            <span className="text-white"> on the</span>
                            <br />
                            <span className="text-white">UN-Nations Emblem Map</span>
                        </h1>

                        <p className="text-lg sm:text-xl text-[#94a3b8] max-w-3xl mx-auto mb-8 leading-relaxed">
                            The only online flight tracking platform that displays real-time flight status 
                            on an <span className="text-white font-medium">Azimuthal Equidistant Map</span> — 
                            the same projection used in the United Nations emblem.
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Button
                                onClick={() => navigate('/tracker')}
                                size="lg"
                                className="bg-[#f07050] hover:bg-[#e06040] text-white px-8 py-6 text-lg btn-primary-glow"
                                data-testid="hero-track-flights-btn"
                            >
                                <Plane className="mr-2 h-5 w-5" />
                                Track Flights Now
                            </Button>
                            <Button
                                onClick={() => navigate('/register')}
                                variant="outline"
                                size="lg"
                                className="border-[#334155] text-white hover:bg-[#334155] px-8 py-6 text-lg"
                                data-testid="hero-get-started-btn"
                            >
                                Get Started Free
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </div>
                    </motion.div>

                    {/* Stats */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
                    >
                        {stats.map((stat, index) => (
                            <div 
                                key={index}
                                className="glass-card rounded-lg p-4"
                            >
                                <p className="text-3xl font-bold text-[#f07050]">{stat.value}</p>
                                <p className="text-sm text-[#94a3b8]">{stat.label}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
                    <div className="w-6 h-10 rounded-full border-2 border-[#334155] flex items-start justify-center p-2">
                        <div className="w-1.5 h-3 bg-[#f07050] rounded-full animate-pulse"></div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                            A Fresh Perspective
                        </h2>
                        <p className="text-lg text-[#94a3b8] max-w-2xl mx-auto">
                            Experience flight tracking like never before with our unique map projection
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="glass-card rounded-xl p-6 group hover:border-[#f07050]/50 transition-colors"
                            >
                                <div className="p-3 bg-[#f07050]/10 rounded-lg w-fit mb-4 group-hover:bg-[#f07050]/20 transition-colors">
                                    <feature.icon className="h-6 w-6 text-[#f07050]" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                                <p className="text-[#94a3b8] leading-relaxed">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Map Preview Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1e293b]/30">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                                The United Nations Map Style
                            </h2>
                            <p className="text-lg text-[#94a3b8] mb-6 leading-relaxed">
                                The Azimuthal Equidistant projection shows all points on Earth at 
                                their true distance and direction from the center point. This is why 
                                it's used in the UN emblem — representing unity and equality.
                            </p>
                            <ul className="space-y-3 mb-8">
                                {[
                                    'True distances from center point',
                                    'North Pole centered view',
                                    'Perfect for visualizing worldwide flights',
                                    'Used by aviation for decades'
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-[#94a3b8]">
                                        <Check className="h-5 w-5 text-[#f07050]" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <Button
                                onClick={() => navigate('/tracker')}
                                className="bg-[#f07050] hover:bg-[#e06040] text-white btn-primary-glow"
                                data-testid="feature-explore-btn"
                            >
                                Explore the Map
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="relative"
                        >
                            <div className="rounded-2xl bg-[#071D35] border border-[#334155] overflow-hidden relative shadow-2xl">
                                <img 
                                    src="/tracker-preview.jpg"
                                    alt="AirlineBound Flight Tracker - 150 Live Flights on Azimuthal Equidistant Map"
                                    className="w-full h-auto object-cover"
                                />
                                {/* Overlay with live indicator */}
                                <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#071D35]/80 backdrop-blur-sm border border-[#334155]">
                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                    <span className="text-xs text-white font-medium">150+ Live Flights</span>
                                </div>
                            </div>
                            {/* Caption */}
                            <p className="text-center text-sm text-[#64748b] mt-4">
                                Real-time flight tracking on the Azimuthal Equidistant projection
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                            Simple Pricing
                        </h2>
                        <p className="text-lg text-[#94a3b8]">
                            Start free, upgrade when you need more
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Free Tier */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="glass-card rounded-xl p-6"
                        >
                            <h3 className="text-2xl font-bold text-white mb-2">Free</h3>
                            <p className="text-[#94a3b8] text-sm mb-4">Perfect for tracking your home country</p>
                            <p className="text-4xl font-bold text-white mb-6">$0</p>
                            <ul className="space-y-2 mb-6">
                                {[
                                    'Azimuthal map view',
                                    'Real-time flight tracking',
                                    'Track 1 country of your choice',
                                    'Ad-supported'
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-[#94a3b8] text-sm">
                                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <Button
                                onClick={() => navigate('/register')}
                                variant="outline"
                                className="w-full border-[#334155] text-white hover:bg-[#334155]"
                                data-testid="pricing-free-btn"
                            >
                                <MapPin className="mr-2 h-4 w-4" />
                                Get Started Free
                            </Button>
                        </motion.div>

                        {/* Share to Save - Middle Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.05 }}
                        >
                            <ShareToSaveCard 
                                onUnlock={(code) => console.log('Discount code unlocked:', code)} 
                            />
                        </motion.div>

                        {/* Premium Tier */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="glass-card rounded-xl p-6 border-[#f07050]/30 relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 px-3 py-1 bg-[#f07050] text-white text-xs font-bold">
                                FULL ACCESS
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Premium</h3>
                            <p className="text-[#94a3b8] text-sm mb-4">For aviation enthusiasts</p>
                            <p className="text-4xl font-bold text-white mb-6">
                                $4.99
                                <span className="text-base text-[#94a3b8] font-normal">/month</span>
                            </p>
                            <ul className="space-y-2 mb-6">
                                {[
                                    'Everything in Free',
                                    'Track ALL countries worldwide',
                                    'Flight path trails',
                                    'Follow flights in real-time',
                                    'No advertisements',
                                    'Cancel anytime'
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-[#94a3b8] text-sm">
                                        <Check className="h-4 w-4 text-[#f07050] flex-shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <Button
                                onClick={() => navigate('/register')}
                                className="w-full bg-[#f07050] hover:bg-[#e06040] text-white btn-primary-glow"
                                data-testid="pricing-premium-btn"
                            >
                                <Globe className="mr-2 h-4 w-4" />
                                Start Premium
                            </Button>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4 border-t border-[#334155]">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <img 
                        src="https://customer-assets.emergentagent.com/job_181a1683-971b-4c0d-99ad-dbc6848d04b9/artifacts/2a99u289_AB%20Header%20Logo%20transparent.png"
                        alt="AirlineBound"
                        className="h-8 w-auto object-contain"
                    />
                    <p className="text-sm text-[#64748b]">
                        © 2024 AirlineBound. Real-time flight tracking on Azimuthal Equidistant Map.
                    </p>
                </div>
            </footer>
        </div>
    );
};
